import type { EntityOptionFieldType } from "@entrilia/shared/api/dictionaryTypes";
import type {
  MetricAggregationType,
  MetricDataType,
  MetricFormatConfiguration,
  MetricInputSettingsConfiguration,
  MetricValueSourceConfiguration,
  NumberDisplayStyle,
} from "@entrilia/shared/api/portfolioMonitoringTypes";
import { allNumberDisplayStyles } from "@entrilia/shared/api/portfolioMonitoringTypes";
import { formatMetricMoneyValue, formatMetricNumberValue } from "@entrilia/shared/utilities/metricFormatters";
import { getValuesFromMultilineValue } from "@entrilia/shared/utilities/stringHelper";
import {
  arrayValidator,
  combineValidators,
  distinctValuesValidator,
  invalidResult,
  maxCharactersValidator,
  nonEmptyArrayValidator,
  requiredValidator,
  uniqueValidator,
  validResult,
} from "@entrilia/shared/utilities/validators";
import type {
  CreateMetricRequest,
  Metric,
  UpdateMetricExtensionConfigurationRequest,
  UpdateMetricRequest,
} from "../../../../../api/types/portfolioMonitoringTypes";

export interface MetricEditForm {
  dataType: MetricDataType;
  name: string;
  description: string;
  category: string;
  aggregationType: MetricAggregationType;
  allowedValuesMemo: string;
  numberDisplayStyle: NumberDisplayStyle | undefined;
  numberOfDecimals: number;
  enableMaxNumberOfSymbols: boolean;
  maxNumberOfSymbols: number;
  isExtended: boolean;
  extensionName: string;
  extensionSource: MetricExtensionSource;
  staticExtensionValuesMemo: string;
}

export type MetricExtensionSource = "static_values" | "custom";

export interface MetricEditorFormState extends MetricEditForm {
  isFormValid: boolean;
  validationErrors: ValidationErrors;
  touchedFields: MetricFormField[];
}

interface ValidationErrors {
  name?: string;
  maxNumberOfSymbols?: string;
  extensionName?: string;
  allowedValuesMemo?: string;
  staticExtensionValuesMemo?: string;
}

export type MetricFormField = keyof MetricEditForm;

export const isValueSourceConfigurationEnabled = (dataType: MetricDataType): boolean => dataType === "Select";

export const isAggregationTypeEnabled = (dataType: MetricDataType): boolean => ["Number", "Money"].includes(dataType);

export const isFormatConfigurationEnabled = (dataType: MetricDataType): boolean =>
  ["Number", "Money"].includes(dataType);

export const isInputSettingsConfigurationEnabled = (dataType: MetricDataType): boolean => dataType === "Text";

export const getAllowedNumberStyles = (dataType: MetricDataType): readonly NumberDisplayStyle[] => {
  switch (dataType) {
    case "Number":
      return allNumberDisplayStyles;
    case "Money":
      return ["Thousands", "Millions", "Billions"];
    default:
      return [];
  }
};

export const extensionSourceOptions: readonly { label: string; value: MetricExtensionSource }[] = [
  { label: "Static values", value: "static_values" },
  { label: "Company-specific values", value: "custom" },
];

export const commonDictionaryOptions: readonly { label: string; value: EntityOptionFieldType }[] = [
  { label: "Countries", value: "Country" },
  { label: "Currencies", value: "Currency" },
  { label: "States", value: "State" },
];

const validateMaxNumberOfSymbols = (value: number) =>
  value > 0 ? validResult() : invalidResult("Value must be greater than 0");

const validateExtensionName = combineValidators(requiredValidator, maxCharactersValidator(50));

const validateAllowedValues = combineValidators(
  nonEmptyArrayValidator("Options cannot be empty"),
  distinctValuesValidator("Options must be unique"),
  arrayValidator(maxCharactersValidator(50), "Invalid option")
);

export const validateForm = (form: MetricEditorFormState, otherMetricNames: string[]): MetricEditorFormState => {
  const validateName = combineValidators(
    requiredValidator,
    uniqueValidator("A metric with this name already exists", otherMetricNames)
  );

  const nameResult = validateName(form.name);

  const inputSettingsResult =
    isInputSettingsConfigurationEnabled(form.dataType) && form.enableMaxNumberOfSymbols
      ? validateMaxNumberOfSymbols(form.maxNumberOfSymbols)
      : validResult();

  const allowedValuesResult = isValueSourceConfigurationEnabled(form.dataType)
    ? validateAllowedValues(getValuesFromMultilineValue(form.allowedValuesMemo))
    : validResult();

  const extensionNameResult = form.isExtended ? validateExtensionName(form.extensionName) : validResult();

  const staticExtensionValuesResult =
    form.isExtended && form.extensionSource === "static_values"
      ? validateAllowedValues(getValuesFromMultilineValue(form.staticExtensionValuesMemo))
      : validResult();

  const validationErrors: ValidationErrors = {
    name: form.touchedFields.includes("name") ? nameResult.error : undefined,
    maxNumberOfSymbols: form.touchedFields.includes("maxNumberOfSymbols") ? inputSettingsResult.error : undefined,
    extensionName: form.touchedFields.includes("extensionName") ? extensionNameResult.error : undefined,
    allowedValuesMemo: form.touchedFields.includes("allowedValuesMemo") ? allowedValuesResult.error : undefined,
    staticExtensionValuesMemo: form.touchedFields.includes("staticExtensionValuesMemo")
      ? staticExtensionValuesResult.error
      : undefined,
  };

  return {
    ...form,
    validationErrors,
    isFormValid:
      nameResult.isValid &&
      inputSettingsResult.isValid &&
      allowedValuesResult.isValid &&
      extensionNameResult.isValid &&
      staticExtensionValuesResult.isValid,
  };
};

const getValueSourceConfiguration = (form: MetricEditForm): MetricValueSourceConfiguration | undefined => {
  if (!isValueSourceConfigurationEnabled(form.dataType)) {
    return undefined;
  }

  return {
    allowedValues: getValuesFromMultilineValue(form.allowedValuesMemo),
  };
};

const getDefaultFormatConfiguration = (form: MetricEditForm): MetricFormatConfiguration | undefined => {
  if (!isFormatConfigurationEnabled(form.dataType)) {
    return undefined;
  }

  return {
    numberDisplayStyle: form.numberDisplayStyle,
    numberOfDecimals: form.numberOfDecimals,
  };
};

const getInputSettingsConfiguration = (form: MetricEditForm): MetricInputSettingsConfiguration | undefined => {
  if (!isInputSettingsConfigurationEnabled(form.dataType)) {
    return undefined;
  }

  return form.enableMaxNumberOfSymbols ? { maxNumberOfSymbols: form.maxNumberOfSymbols } : undefined;
};

const getExtensionConfigurationUpdate = (
  form: MetricEditForm
): UpdateMetricExtensionConfigurationRequest | undefined => {
  if (!form.isExtended) {
    return undefined;
  }

  switch (form.extensionSource) {
    case "static_values":
      return {
        extensionName: form.extensionName,
        allowedValues: getValuesFromMultilineValue(form.staticExtensionValuesMemo),
      };
    case "custom": {
      return {
        extensionName: form.extensionName,
      };
    }
    default:
      return undefined;
  }
};

export const mapFormToCreateMetricRequest = (form: MetricEditForm): CreateMetricRequest => ({
  dataType: form.dataType,
  name: form.name.trim(),
  description: form.description.trim() || undefined,
  category: form.category.trim() || undefined,
  aggregationType: isAggregationTypeEnabled(form.dataType) ? form.aggregationType : "EndOfPeriod",
  valueSource: getValueSourceConfiguration(form),
  defaultFormat: getDefaultFormatConfiguration(form),
  inputSettings: getInputSettingsConfiguration(form),
  extensionConfiguration: getExtensionConfigurationUpdate(form),
});

export const mapFormToUpdateMetricRequest = (form: MetricEditForm): UpdateMetricRequest => ({
  name: form.name.trim(),
  description: form.description.trim(),
  category: form.category.trim(),
  aggregationType: isAggregationTypeEnabled(form.dataType) ? form.aggregationType : "EndOfPeriod",
  valueSource: getValueSourceConfiguration(form),
  defaultFormat: getDefaultFormatConfiguration(form),
  inputSettings: getInputSettingsConfiguration(form),
  extensionConfiguration: getExtensionConfigurationUpdate(form),
});

export const getInitialMetricEditorFormState = (dataType: MetricDataType): MetricEditorFormState => ({
  dataType,
  name: "",
  description: "",
  category: "",
  aggregationType: "EndOfPeriod",
  allowedValuesMemo: "",
  numberDisplayStyle: undefined,
  numberOfDecimals: 0,
  enableMaxNumberOfSymbols: false,
  maxNumberOfSymbols: 400,
  isFormValid: false,
  validationErrors: {},
  touchedFields: [],
  isExtended: false,
  extensionName: "",
  extensionSource: "static_values",
  staticExtensionValuesMemo: "",
});

export const getInitialMetricEditorFormStateFromMetric = (metric: Metric): MetricEditorFormState => ({
  dataType: metric.dataType,
  name: metric.name,
  description: metric.description ?? "",
  category: metric.category ?? "",
  aggregationType: metric.aggregationType,
  allowedValuesMemo: metric.valueSource?.allowedValues?.join("\n") ?? "",
  numberDisplayStyle: metric.defaultFormat?.numberDisplayStyle,
  numberOfDecimals: metric.defaultFormat?.numberOfDecimals ?? 0,
  enableMaxNumberOfSymbols: Boolean(metric.inputSettings?.maxNumberOfSymbols),
  maxNumberOfSymbols: metric.inputSettings?.maxNumberOfSymbols ?? 400,
  isFormValid: true,
  validationErrors: {},
  touchedFields: [],
  isExtended: metric.isExtended,
  extensionName: metric.extensionConfiguration?.extensionName ?? "",
  extensionSource:
    !metric.isExtended || metric.extensionConfiguration?.staticExtensions?.length ? "static_values" : "custom",
  staticExtensionValuesMemo: (metric.extensionConfiguration?.staticExtensions.map((ext) => ext.value) ?? []).join("\n"),
});

export const formatPreviewValue = (form: MetricEditForm): string => {
  const getSampleValue = () => {
    const sampleValue = 2345.678910234567;
    switch (form.numberDisplayStyle) {
      case "Percentage":
        return sampleValue / 10_000;
      case "Multiple":
        return sampleValue / 1000;
      case "Thousands":
        return sampleValue * 10;
      case "Millions":
        return sampleValue * 10_000;
      case "Billions":
        return sampleValue * 10_000_000;
      default:
        return sampleValue;
    }
  };

  const format: MetricFormatConfiguration = {
    numberDisplayStyle: form.numberDisplayStyle,
    numberOfDecimals: form.numberOfDecimals,
  };

  switch (form.dataType) {
    case "Number": {
      return formatMetricNumberValue(getSampleValue(), format);
    }
    case "Money": {
      return formatMetricMoneyValue(getSampleValue(), "USD", format);
    }
    default: {
      return "";
    }
  }
};
